import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PhoneIcon } from '@heroicons/react/24/outline';
import config from '../../config/config.json';

interface CallConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const CallConfirmationDialog: React.FC<CallConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  const isBusinessHours = () => {
    const now = new Date();
    const myanmarTime = new Date(
      now.toLocaleString('en-US', { timeZone: config.callCenter.businessHours.timezone }),
    );

    const currentDay = myanmarTime.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const currentHour = myanmarTime.getHours();
    const currentMinute = myanmarTime.getMinutes();
    const currentTime = currentHour * 60 + currentMinute; // Convert to minutes

    const startTime =
      parseInt(config.callCenter.businessHours.startTime.split(':')[0]) * 60 +
      parseInt(config.callCenter.businessHours.startTime.split(':')[1]);
    const endTime =
      parseInt(config.callCenter.businessHours.endTime.split(':')[0]) * 60 +
      parseInt(config.callCenter.businessHours.endTime.split(':')[1]);

    const isWorkingDay = config.callCenter.businessHours.workingDays.includes(currentDay);
    const isWorkingTime = currentTime >= startTime && currentTime <= endTime;

    return isWorkingDay && isWorkingTime;
  };

  const handleCall = () => {
    onConfirm();
    window.location.href = `tel:${config.callCenter.phoneNumber}`;
  };

  if (!isOpen) return null;

  const businessHoursActive = isBusinessHours();

  return createPortal(
    <div className="fixed inset-0 will-change-transform transform-gpu z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      {/* Dialog */}
      <div className="bg-white/95 backdrop-blur-md border border-white/20 shadow-2xl rounded-2xl p-4 sm:p-6 w-full max-w-md mx-4">
        {/* Content */}
        <div className="text-center">
          {/* Phone Icon */}
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <PhoneIcon className="w-8 h-8 text-blue-600" />
          </div>

          {/* Title */}
          <h3 className="text-xl font-semibold mb-2 text-gray-900">{t('call_dialog.title')}</h3>

          {/* Phone Number */}
          <p className="text-gray-600 mb-6">{config.callCenter.phoneNumber}</p>

          {/* Business Hours Warning */}
          {!businessHoursActive && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                {t('call_dialog.outside_business_hours')}
                <br />
                <span className="font-medium">{t('call_dialog.business_hours')}</span>
              </p>
            </div>
          )}

          {businessHoursActive && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-green-800">{t('call_dialog.currently_open')}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-600 text-white py-3 rounded-xl font-medium hover:bg-gray-700 transition-colors"
            >
              {t('call_dialog.cancel')}
            </button>
            <button
              onClick={handleCall}
              className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <PhoneIcon className="h-4 w-4" />
              {t('call_dialog.call')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CallConfirmationDialog;
